/** @jsxImportSource ./simboluri-jsx */
import { ReactNode, useState } from "react";
import { useTn } from "../i18n";
import { numar, useT, useTx } from "../i18n";
import type { Cheie } from "../i18n";
import HoverCard from "./HoverCard";

// Explicațiile scrise ale pașilor — la nivelul unui student la construcții.
// Regula casei: orice cifră afișată trebuie să spună CE reprezintă, dacă a fost
// INTRARE sau DECIZIE a motorului, CU CE FORMULĂ s-a obținut (cu valorile
// proiectului substituite) și CUM se validează (pragul + normativul).
//
//   <Eticheta tip="intrare|decizie|masurat" />  — marchează proveniența
//   <Legenda ... />                             — dicționarul unui tabel:
//                                                 o intrare per COLOANĂ
//   <Pas ... />                                 — introducerea unui pas/tab

export function Eticheta({ tip }: { tip: "intrare" | "decizie" | "masurat" }) {
  const t = useT();
  return (
    <span className={`ex-tag ex-tag-${tip}`} title={t(`struct.expl.${tip}_titlu` as Cheie)}>
      {t(`struct.expl.${tip}` as Cheie)}
    </span>
  );
}

export interface Coloana {
  nume: string;
  ce: ReactNode;               // ce reprezintă, în cuvinte
  formula?: ReactNode;         // cum se calculează
  sursa?: ReactNode;           // de unde vin datele care intră în ea
  validare?: ReactNode;        // pragul și normativul
}

export function Legenda({
  titlu,
  intro,
  coloane,
  deschisImplicit = false,
}: {
  titlu?: string;
  intro?: ReactNode;
  coloane: Coloana[];
  deschisImplicit?: boolean;
}) {
  const t = useT();
  const [deschis, setDeschis] = useState(deschisImplicit);
  return (
    <div className="ex-legenda">
      <button className="ex-toggle" onClick={() => setDeschis(!deschis)}>
        {deschis ? "▾" : "▸"} {titlu ?? t("struct.expl.legenda_titlu")}
        <span className="sg-muted">
          {t("struct.expl.nr_coloane", { n: coloane.length })}
        </span>
      </button>
      {deschis && (
        <>
          {intro && <p className="sg-muted ex-intro">{intro}</p>}
          <table className="sg-table ex-tabel">
            <thead>
              <tr>
                <th>{t("struct.expl.col_coloana")}</th>
                <th>{t("struct.expl.col_ce")}</th>
                <th>{t("struct.expl.col_cum")}</th>
                <th>{t("struct.expl.col_validare")}</th>
              </tr>
            </thead>
            <tbody>
              {coloane.map((c) => (
                <tr key={c.nume}>
                  <td><strong>{c.nume}</strong></td>
                  <td>
                    {c.ce}
                    {c.sursa && (
                      <div className="sg-muted ex-sursa">{t("struct.expl.sursa")}{c.sursa}</div>
                    )}
                  </td>
                  <td className="ex-formula">{c.formula ?? "—"}</td>
                  <td>
                    {c.validare ?? (
                      <span className="sg-muted">{t("struct.expl.informativ")}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// Blocul de explicație al unui pas. STRÂNS implicit:
// explicațiile ocupau două pagini înainte să ajungi la cifre. Textul nu se
// pierde — se deschide dintr-un clic pe titlu, iar cine îl vrea permanent
// deschis dă `deschisImplicit`. Rezumatul de o linie (`rezumat`) rămâne
// vizibil, ca titlul singur să nu fie o cutie neagră.
export function Pas({
  titlu,
  rezumat,
  deschisImplicit = false,
  children,
}: {
  titlu: string;
  rezumat?: ReactNode;
  deschisImplicit?: boolean;
  children: ReactNode;
}) {
  const [deschis, setDeschis] = useState(deschisImplicit);
  return (
    <div className="ex-pas">
      <button className="ex-pas-titlu ex-toggle"
              aria-expanded={deschis}
              onClick={() => setDeschis(!deschis)}>
        {deschis ? "▾" : "▸"} {titlu}
      </button>
      {!deschis && rezumat && (
        <div className="ex-pas-rezumat sg-muted">{rezumat}</div>
      )}
      {deschis && <div className="ex-pas-body">{children}</div>}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Bara «valoarea aplicată față de limită»
// ---------------------------------------------------------------------------
// O cifră lângă altă cifră nu spune care e care. Bara spune, fără hover: plinul
// e cât se folosește din limită, capătul e limita. Peste 100 % se colorează
// roșu. Se folosește pe cardurile care au și valoare, și prag.

export function BaraFataDeLimita({
  valoare,
  limita,
  unitate = "",
  eticheta,
}: {
  valoare?: number | null;
  limita?: number | null;
  unitate?: string;
  eticheta?: string;
}) {
  const t = useT();
  if (valoare == null || limita == null || limita <= 0) return null;
  const pct = (valoare / limita) * 100;
  const depaseste = pct > 100;
  return (
    <div className="lm-bara"
         title={t("struct.expl.bara_titlu", { valoare, limita, unitate })}>
      <div className="lm-sina">
        <div
          className={depaseste ? "lm-plin lm-depasit" : "lm-plin"}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <span className={depaseste ? "lm-pct st-bad" : "lm-pct"}>
        {t("struct.expl.pct_eticheta", {
          pct: pct.toFixed(0),
          eticheta: eticheta ?? t("struct.expl.din_limita"),
        })}
      </span>
    </div>
  );
}

// Verdictul unei verificări: nu doar ✓/✗, ci comparația care l-a produs.
// La hover arată: ce s-a comparat, cu ce prag, de unde vine pragul și cât e
// rezerva. Regula casei — un ✗ trebuie să spună singur DE CE e ✗, iar un ✓
// trebuie să spună CUM s-a verificat.

export function Verdict({
  ok,
  valoare,
  prag,
  unitate = "",
  operator = "≤",
  ce,
  regula,
  referinta,
  zecimale = 1,
}: {
  ok?: boolean | null;
  valoare?: number | null;
  prag?: number | null;
  unitate?: string;
  operator?: "≤" | "≥";
  ce: ReactNode;            // ce mărime se verifică
  regula?: ReactNode;       // cum se obține pragul
  referinta?: string;       // normativul
  zecimale?: number;
}) {
  const t = useT();
  const tx = useTx();
  const f = (x?: number | null) =>
    x == null ? "—" : numar(x, {
      minimumFractionDigits: zecimale, maximumFractionDigits: zecimale });
  // rezerva: cât la sută mai e până la prag (pozitiv = mai e loc)
  const rezerva = valoare != null && prag != null && valoare !== 0
    ? (operator === "≤" ? prag / valoare - 1 : valoare / prag - 1) * 100
    : null;

  if (ok == null) return <span className="sg-muted">—</span>;

  return (
    <HoverCard
      clasaAncora="fx-info"
      latime={380}
      eticheta={t(ok ? "struct.expl.trece_eticheta" : "struct.expl.pica_eticheta")}
      continut={
        <>
          <span className="fx-tip-title">
            {t(ok ? "struct.expl.trece" : "struct.expl.pica")}
          </span>
          <span className="fx-tip-block">
            <span className="fx-tip-label">{t("struct.expl.ce_se_verifica")}</span>
            <span>{ce}</span>
          </span>
          <span className="fx-tip-block">
            <span className="fx-tip-label">{tx("struct.expl.comparatia")}</span>
            <span className="ex-comparatie">
              <strong>{f(valoare)}{unitate && ` ${unitate}`}</strong>
              {" "}
              <span className={ok ? "st-ok" : "st-bad"}>
                {ok ? operator : (operator === "≤" ? ">" : "<")}
              </span>
              {" "}
              <strong>{f(prag)}{unitate && ` ${unitate}`}</strong>
              {rezerva != null && (
                <div className="sg-muted">
                  {rezerva >= 0
                    ? t("struct.expl.rezerva", { n: rezerva.toFixed(0) })
                    : t("struct.expl.depasit", { n: Math.abs(rezerva).toFixed(0) })}
                </div>
              )}
            </span>
          </span>
          {regula && (
            <span className="fx-tip-block">
              <span className="fx-tip-label">{t("struct.expl.de_unde_prag")}</span>
              <span>{regula}{referinta && <> — <em>{referinta}</em></>}</span>
            </span>
          )}
          {!ok && (
            <span className="fx-tip-block">
              <span className="fx-tip-label">{t("struct.expl.ce_inseamna")}</span>
              <span>{t("struct.expl.ce_inseamna_text")}</span>
            </span>
          )}
        </>
      }
    >
      <span className={ok ? "st-ok ex-verdict" : "st-bad ex-verdict"}>
        {ok ? "✓" : "✗"}
      </span>
    </HoverCard>
  );
}

// Bannerele «două modele» și «două stări de rigiditate» au fost înlocuite pe
// de cardul unic al modelelor (`ModeleRigiditate.tsx`): trei
// modele, câte unul pentru fiecare întrebare, cu datele fiecărui pas.

// ---------------------------------------------------------------------------
// DIVERGENȚELE de metodă, marcate ÎN FORMULĂ
// ---------------------------------------------------------------------------
//
// Regula: acolo unde metoda biroului diferă de normativ, formula NU se
// rescrie curat — se scrie cu termenul care lipsește, TĂIAT, cu un marker
// care duce la explicație, și cu valoarea pe proiectul curent dacă termenul
// s-ar aplica. O divergență fără cifră nu spune nimic.
//
// Verdictul («trece / nu trece») și divergența («aici metoda diferă de cod»)
// sunt două lucruri diferite și nu au voie să arate la fel: verdictul e
// verde/roșu, divergența e violet.

export interface Divergenta {
  cod: string;
  titlu: string;
  termen?: string;
  implementat: string;
  normativ: string;
  de_ce?: string;
  impact?: string;
  urmam?: string;
}

export function MarkerDivergenta({ cod }: { cod: string }) {
  const t = useT();
  return (
    <a href={`#divergenta-${cod}`} className="ex-div-marker"
       title={t("struct.div.marker_titlu", { cod })}>
      ⓔ{cod.replace(/^E/, "")}
    </a>
  );
}

// Un termen care NU se aplică, păstrat vizibil în formulă
export function TermenOmis({
  cod, children, alternativa,
}: {
  cod: string;
  children: ReactNode;
  alternativa?: ReactNode;   // cât ar ieși dacă s-ar aplica
}) {
  return (
    <span className="ex-omis">
      <s>{children}</s>
      <MarkerDivergenta cod={cod} />
      {alternativa && <span className="ex-omis-alt">{alternativa}</span>}
    </span>
  );
}

export function Divergente({
  divergente,
  titlu,
}: {
  divergente?: Divergenta[];
  titlu?: string;
}) {
  const t = useT();
  const tn = useTn();
  const tx = useTx();
  if (!divergente || divergente.length === 0) return null;
  return (
    <div className="st-findings ex-div-wrap">
      <h3>
        {titlu ?? t("struct.div.titlu")}
        <span className="sg-muted" style={{ fontWeight: 400 }}>
          {tn("struct.div.puncte#n", divergente.length)}
        </span>
      </h3>
      <p className="sg-muted ex-intro">
        {tx("struct.div.intro")}
      </p>
      {divergente.map((d) => (
        <div key={d.cod} className="ex-div-item" id={`divergenta-${d.cod}`}>
          <div className="am-head">
            <span className="ex-div-cod">ⓔ{d.cod.replace(/^E/, "")}</span>
            <strong>{d.titlu}</strong>
            {d.termen && <code className="ex-div-termen">{d.termen}</code>}
            {d.urmam && (
              <span className="am-pref">{t("struct.div.urmam", { ce: d.urmam })}</span>
            )}
          </div>
          <div className="am-cols">
            <div>
              <span className="am-eticheta">{t("struct.div.implementat")}</span>
              <div>{d.implementat}</div>
            </div>
            <div>
              <span className="am-eticheta">{t("struct.div.normativ")}</span>
              <div>{d.normativ}</div>
            </div>
          </div>
          {d.de_ce && (
            <div className="sg-muted ex-div-dece">
              {t("struct.div.de_ce", { motiv: String(d.de_ce) })}
            </div>
          )}
          {d.impact && (
            <div className="am-impact">
              <span className="am-eticheta">{t("struct.div.impact")}</span>
              {d.impact}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
